import { CaseUser } from "./caseUser"
import { DataType } from "./dataType"
import { FieldTypeId } from "./fieldTypes"
import { Operator } from "./insights/operators"
import { TenantFeatures } from "./tenantFeatures"
import { SourceTypeLabels } from "./sourceType"
import { Field } from "./field"

interface CaseColumn {
	key: string;
	label: string;
	description?: string | null;
	dataType: string;
	comparisonType?: DataType;
	fieldDataType?: DataType;
	rounding?: string;

	filterKey?: string;
	displayKey?: string;
	columnValues?: (api: any) => Promise<any[]>;

	listLabel?: string;
	listItemType?: string;

	sortable?: boolean;
	value?: any;
	valueObject? :any;
	valueLabel?: string;

	definitionId?: 	string | null;
	definitionFields?: CaseColumn[];
	dataSourceId?: 	string | null;
	dynamicField: boolean;

	fieldExtensions?: any;
	extensions?: { [key: string]: any }[];
	operators?: Operator[];

	fieldId?: string | null;

	adminOnly?: boolean;
	requiredFeature?: TenantFeatures
	isReadonly?: boolean

	group?: (record: any, groupByValue: any) => { key: string, label: string, value: any }[]
}

const usersPlusDynamic = (api: any) => {
	return new Promise(async (resolve) => {
		const users = await api.usersGet()
		resolve([
			{
				id: "[Me]",
				name: "Me",
				dynamicValue: true
			},
			...users
		])
	})
}

const cachedUsersPlusDynamic: (users: any[], fetchUsers?: () => Promise<any>) => Promise<any[]> = async (users: any[], fetchUsers?: () => Promise<any>) => {
	let userOptions = users
	if ((!users || users.length === 0) && fetchUsers) {
		userOptions = await fetchUsers()
	}

	return [
		{
			id: "[Me]",
			name: "Me",
			dynamicValue: true
		},
		...userOptions
	]
}

const caseColumns = [
	{
		key: "caseNumber",
		label: "Case #",
		dataType: FieldTypeId.Text,
		fieldDataType: DataType.string,
		operators: [Operator.Equals, Operator.NotEqual],
		isReadonly: true
	},
	{
		key: "title",
		label: "Title",
		dataType: FieldTypeId.Text,
		fieldDataType: DataType.string,
		sortable: true,
		operators: [Operator.Contains],
		isReadonly: false
	},
	{
		key: "summary",
		label: "Summary",
		fieldDataType: DataType.string,
		dataType: FieldTypeId.TextArea,
		isReadonly: false
	},
	{
		key: "templateId",
		label: "Template",
		dataType: FieldTypeId.List,
		filterKey: "templateId",
		displayKey: "templateTitle",
		columnValues: (api: any) => api.categories(),
		listLabel: "title",
		sortable: true,
		operators: [Operator.Equals, Operator.NotEqual],
		fieldDataType: DataType.array,
		isReadonly: true // barring triage
	},
	{
		key: "areas",
		label: "Areas",
		dataType: FieldTypeId.ListMultiple,
		fieldDataType: DataType.arrayMultiple,
		listLabel: "title",
		columnValues: (api: any) => api.areaList(),
		operators: [Operator.Contains, Operator.IsSet, Operator.NotSet],
		isReadonly: false
	},
	{
		key: "created",
		label: "Created",
		dataType: FieldTypeId.DateTime,
		sortable: true,
		fieldDataType: DataType.dateTime,
		operators: [Operator.GreaterThan, Operator.LessThan, Operator.Equals, Operator.NotEqual, Operator.DateRange],
		isReadonly: true
	},
	{
		key: "lastUpdated",
		label: "Last Updated",
		dataType: FieldTypeId.DateTime,
		fieldDataType: DataType.dateTime,
		operators: [Operator.GreaterThan, Operator.LessThan, Operator.Equals, Operator.NotEqual, Operator.DateRange],
		sortable: true,
		isReadonly: true
	},
	{
		key: "isClosed",
		label: "Closed",
		dataType: FieldTypeId.Checkbox,
		fieldDataType: DataType.boolean,
		isReadonly: true
	},
	{
		key: "closed",
		label: "Closed At",
		dataType: FieldTypeId.DateTime,
		fieldDataType: DataType.dateTime,
		sortable: true,
		isReadonly: true
	},
	{
		key: "openForDays",
		label: "Days Open",
		dataType: FieldTypeId.Number,
		fieldDataType: DataType.number,
		sortable: true,
		rounding: "floor",
		isReadonly: true
	},
	{
		key: "statusId",
		label: "Status",
		dataType: FieldTypeId.List,
		filterKey: "statusId",
		displayKey: "statusTitle",
		listLabel: "title",
		fieldDataType: DataType.array,
		columnValues: (api: any) => api.statuses(),
		sortable: true,
		isReadonly: false
	},
	{
		key: "severityId",
		label: "Severity",
		dataType: FieldTypeId.List,
		filterKey: "severityId",
		displayKey: "severityTitle",
		listLabel: "title",
		fieldDataType: DataType.array,
		columnValues: (api: any) => api.severities(),
		sortable: true,
		isReadonly: false
	},
	{
		key: "isCase",
		label: "Promoted to Case",
		dataType: FieldTypeId.Checkbox,
		fieldDataType: DataType.boolean,
		sortable: true,
		isReadonly: true
	},
	{
		key: "assigneeId",
		label: "Assignee",
		dataType: FieldTypeId.User,
		columnValues: usersPlusDynamic,
		comparisonType: DataType.array,
		fieldDataType: DataType.array,
		listLabel: "name",
		operators: [Operator.Equals, Operator.NotEqual, Operator.IsSet, Operator.NotSet],
		group: (record: any, groupByValue: any, cache: { [key:string]: any }) => {
			if (!groupByValue) {
				return [
					{
						key: "none",
						label: "None set",
						value: null
					}
				]
			} else if (typeof groupByValue === "string") {
				const userValue = cache[groupByValue]
					? cache[groupByValue]
					: cache[groupByValue] = record.users.find((u: CaseUser) => u.id === groupByValue)
				return [{
					key: groupByValue,
					label: userValue ? userValue.name : groupByValue,
					value: userValue
				}]
			}

			return []
		},
		isReadonly: true
	},
	{
		key: "users",
		label: "Collaborators",
		dataType: FieldTypeId.ListMultiple,
		listLabel: "name",
		listItemType: "user",
		columnValues: usersPlusDynamic,
		fieldDataType: DataType.arrayMultiple,
		value: null,
		valueObject: null,
		operators: [Operator.Contains],
		isReadonly: true
	},
	{
		key: "pendingTeams",
		label: "Awaiting Team",
		dataType: FieldTypeId.ListMultiple,
		fieldDataType: DataType.arrayMultiple,
		columnValues: (api: any) => api.teamsGet(),
		listLabel: "title",
		operators: [Operator.Contains],
		isReadonly: true
	},
	{
		key: "tags",
		label: "Tags",
		dataType: FieldTypeId.ListMultiple,
		fieldDataType: DataType.arrayMultiple,
		columnValues: (api: any) => api.tagList(),
		listLabel: "title",
		operators: [Operator.Contains],
		isReadonly: false
	},
	{
		key: "hiddenFromInsights",
		label: "Hidden From Insights",
		dataType: FieldTypeId.Checkbox,
		fieldDataType: DataType.boolean,
		operators: [Operator.IsSet, Operator.NotSet],
		adminOnly: true,
		isReadonly: false
	},
	{
		key: "deleted",
		label: "Deleted At",
		dataType: FieldTypeId.DateTime,
		sortable: true,
		fieldDataType: DataType.dateTime,
		operators: [Operator.GreaterThan, Operator.LessThan, Operator.Equals, Operator.NotEqual, Operator.DateRange, Operator.IsSet, Operator.NotSet],
		isReadonly: true
	}
] as CaseColumn[]

const cadenceEnabled = {
	key: "cadenceEnabled",
	label: "Cadence Enabled",
	dataType: FieldTypeId.Checkbox,
	fieldDataType: DataType.boolean,
	operators: [Operator.IsSet, Operator.NotSet],
	requiredFeature: TenantFeatures.CadenceEnabledColumn,
	dynamicField: false,
	isReadonly: false
}

const createdBy = {
	key: "createdBy",
	label: "Created By",
	filterKey: "createdBy",
	displayKey: "createdBy",
	dataType: FieldTypeId.AuditUser,
	fieldDataType: DataType.auditUser,
	operators: [Operator.Equals, Operator.IsSet, Operator.NotSet],
	requiredFeature: TenantFeatures.FilterByBuiltInUsers,
	dynamicField: false,
	isReadonly: true
}

const lastUpdatedBy = {
	key: "lastUserUpdatedBy",
	label: "Last Updated By",
	filterKey: "lastUserUpdatedBy",
	displayKey: "lastUserUpdatedBy",
	dataType: FieldTypeId.AuditUser,
	fieldDataType: DataType.auditUser,
	operators: [Operator.Equals, Operator.IsSet, Operator.NotSet],
	requiredFeature: TenantFeatures.FilterByBuiltInUsers,
	dynamicField: false,
	isReadonly: true
}

const closedBy = {
	key: "closedBy",
	label: "Closed By",
	filterKey: "closedBy",
	displayKey: "closedBy",
	dataType: FieldTypeId.AuditUser,
	fieldDataType: DataType.auditUser,
	operators: [Operator.Equals, Operator.IsSet, Operator.NotSet],
	requiredFeature: TenantFeatures.FilterByBuiltInUsers,
	dynamicField: false,
	isReadonly: true
}

const forecasted = {
	key: "forecasted",
	label: "Forecasted",
	filterKey: "forecasted",
	displayKey: "forecasted",
	dataType: FieldTypeId.Checkbox,
	fieldDataType: DataType.boolean,
	operators: [Operator.IsSet, Operator.NotSet],
	requiredFeature: TenantFeatures.ForecastedColumn,
	dynamicField: false,
	isReadonly: true
}

const sourceType = {
	key: "sourceType",
	label: "Source Type",
	filterKey: "sourceType",
	displayKey: "sourceType",
	dataType: "list",
	fieldDataType: DataType.array,
	operators: [Operator.IsSet, Operator.NotSet, Operator.Equals, Operator.NotEqual],
	requiredFeature: TenantFeatures.CadenceId,
	dynamicField: false,
	isReadonly: true,
	columnValues: () => Promise.resolve(SourceTypeLabels)
}

const cadenceNumber: CaseColumn = {
	key: "cadenceId",
	label: "Cadence #",
	filterKey: "cadenceId",
	displayKey: "cadenceNumber",
	dataType: "list",
	fieldDataType: DataType.array,
	operators: [Operator.IsSet, Operator.NotSet, Operator.Equals, Operator.NotEqual],
	requiredFeature: TenantFeatures.CadenceId,
	dynamicField: false,
	columnValues: (api: any) => api.cadenceInstances(),
	listLabel: "cadenceNumber"
}

const pendingTriage: CaseColumn = {
	key: "pendingTriage",
	label: "Pending Triage",
	filterKey: "pendingTriage",
	displayKey: "pendingTriage",
	dataType: "checkbox",
	fieldDataType: DataType.boolean,
	operators: [Operator.IsSet, Operator.NotSet],
	requiredFeature: TenantFeatures.Triage,
	dynamicField: false,
	isReadonly: true
}

const requiredFeatureColumns: CaseColumn[] = [
	cadenceEnabled, createdBy, lastUpdatedBy, closedBy, forecasted, sourceType,
	cadenceNumber, pendingTriage
]

const isBuiltInCaseColumn = (columnKey: string) => {
	return caseColumns.some(c => c.key === columnKey) ||
		requiredFeatureColumns.some(c => c.key === columnKey)
}

const getBuiltInCaseColumn = (columnKey: string) => {
	return caseColumns.find(c => c.key === columnKey) ||
		requiredFeatureColumns.find(c => c.key === columnKey)
}

export function caseColumnToField (caseColumn: CaseColumn): Field | null {
	if (!isBuiltInCaseColumn(caseColumn.key)) {
		return {
			id: caseColumn.fieldId,
			title: caseColumn.label,
			dataType: caseColumn.dataType
		} as Field
	}

	return {
		id: caseColumn.key,
		title: caseColumn.label,
		dataType: caseColumn.dataType
	} as Field
}

export {
	CaseColumn, caseColumns, usersPlusDynamic, cachedUsersPlusDynamic, cadenceEnabled, createdBy, lastUpdatedBy, closedBy,
	requiredFeatureColumns, isBuiltInCaseColumn, getBuiltInCaseColumn, cadenceNumber, sourceType,
	pendingTriage, forecasted
}
